import clsx from "clsx";
import { FormattedLearnerSubscriptionPlan } from "../../../types";
import { formatPrice } from "../../../utils/formatPrice";
import { DiscountBanner } from "./DiscountBanner";
import { useHomepageSignupContext } from "../HomepageSignupContext";
import { getActualPriceAmount, getStrikethroughPriceAmount } from "./pricing";

export const PriceCard: React.FC<{
  plan: FormattedLearnerSubscriptionPlan;
  title: string;
  type: "month" | "year";
  discount?: number;
  displayOnly?: boolean;
}> = ({ plan, title, type, discount, displayOnly = false }) => {
  const {
    numLearners,
    selectedPlan,
    setSelectedPlan,
    showPromoPrice,
    referral,
  } = useHomepageSignupContext();
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!displayOnly && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setSelectedPlan(plan);
    }
  };

  const billingCopy = type === "month" ? "billed monthly" : `billed yearly`;

  const showPromoPriceFinal = showPromoPrice || referral;

  const getPlanTypeName = () => {
    if (referral) {
      if (
        plan.interval === "monthly" &&
        referral.monthlyCoupon.duration === "repeating"
      ) {
        return ` first ${referral.monthlyCoupon.duration_in_months} months`;
      }
      if (
        plan.interval === "annual" &&
        referral.annualCoupon.duration === "once"
      ) {
        return " first year";
      }
      throw new Error("Unexpected coupon duration");
    }
    return `/${type}`;
  };

  const Discount = () => {
    if (referral) {
      if (plan.interval === "monthly") {
        if (referral.monthlyCoupon.duration === "repeating") {
          return (
            <DiscountBanner>
              {referral.monthlyCoupon.percent_off}% off the first{" "}
              {referral.monthlyCoupon.duration_in_months} months
            </DiscountBanner>
          );
        }
        throw new Error("Unexpected coupon duration");
      }
      if (plan.interval === "annual") {
        return (
          <DiscountBanner>
            {referral.annualCoupon.percent_off}% off the first year
          </DiscountBanner>
        );
      }
    }
    if (discount) {
      return <DiscountBanner discount={discount} />;
    }
    return null;
  };

  return (
    <div
      role={displayOnly ? "figure" : "button"}
      onClick={() => !displayOnly && setSelectedPlan(plan)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={clsx(
        "relative flex-1 rounded-2xl border-[3px] bg-white p-6 text-dm-charcoal-800",
        displayOnly || plan._id === selectedPlan?._id
          ? "border-dm-brand-blue-500"
          : "border-dm-brand-blue-100"
      )}
    >
      <h5 className="mb-4 text-base font-bold">{title}</h5>
      {showPromoPriceFinal && (
        <div>
          <span className="relative font-serif text-2xl font-bold text-[#838B95]">
            {formatPrice({
              value: getStrikethroughPriceAmount(plan, referral, numLearners),
              currency: plan.learnerPrice.currency,
            })}
            <span className="absolute -left-1 -right-3 top-[14px] h-[2px] -rotate-[6deg] bg-[#838B95]" />
          </span>
          <span className="ml-[2px] text-dm-charcoal-500">/{type}</span>
        </div>
      )}
      <div className={clsx(showPromoPriceFinal && "text-[#10862F]")}>
        <span className="font-serif text-3xl font-bold !leading-10">
          {formatPrice({
            value: getActualPriceAmount(plan, referral, numLearners),
            currency: plan.learnerPrice.currency,
          })}
        </span>
        <span>{getPlanTypeName()}</span>
      </div>
      <p className="text-sm !leading-[26px] text-dm-charcoal-500">
        {billingCopy} for {numLearners} learner{numLearners > 1 ? "s" : ""}
      </p>
      <Discount />
    </div>
  );
};
